export type StationSource =
  | "radio-browser"
  | "sr"
  | "nrk"
  | "rtp"
  | "abc"
  | "rnz"
  | "yle"
  | "custom"
  | "podcast"
  | "bauer"
  | "viaplay"
  | "dbmedia"
  | "kexp"
  | "bbc"
  | "nent"
  | "independent"
  | "icecast"
  | "creator";

export interface Station {
  id: string; // Global unique ID (e.g., "sr-132" or UUID)
  name: string;
  country: string; // Full Country Name
  countryCode: string; // ISO-2 (e.g., "SE", "US")
  state?: string; // Region/State (e.g. "California", "Skåne")
  provider: StationSource;
  streamUrl: string;
  website?: string;
  logoUrl?: string;
  bitrate: number; // kbps
  codec?: string; // e.g. MP3, AAC+
  language?: string[]; // e.g. ["english", "spanish"]
  tags: string[]; // Array of strings
  votes: number; // Popularity score (Total clicks)
  clickTrend: number; // Daily trend clicks
  onlineStatus?: "online" | "offline" | "unknown";
  color?: string;
  description?: string; // Added for podcasts
  lastEpisodeTitle?: string; // Added for podcasts
  lastEpisodeDate?: string; // Added for podcasts
  episodes?: Episode[]; // The full list of episodes
  isLoadingData?: boolean; // NEW: For optimistic UI loading
  stationType?: "mainstream" | "online" | "unknown"; // NEW: Classification
  feedUrl?: string; // RSS feed URL for podcasts
  url?: string; // Fallback URL
}

// Re-export old name if needed for strict compatibility during refactor,
// but mapped to new interface structure where possible.
export type RadioStation = Station;

export interface ChatMessage {
  id: string;
  role: "user" | "model";
  text: string;
  isAudioResponse?: boolean;
  timestamp: number;
}

export enum DJMode {
  IDLE = "IDLE",
  LISTENING = "LISTENING",
  THINKING = "THINKING",
  SPEAKING = "SPEAKING",
}

export interface SearchResult {
  title: string;
  url: string;
}

export type DJPersona = "upbeat" | "chill" | "knowledgeable";

export type ViewMode =
  | "landing"
  | "home"
  | "search"
  | "station"
  | "favorites"
  | "history"
  | "discover"
  | "podcast-details"
  | "api-docs"
  | "library"
  | "login"
  | "register"
  | "account"
  | "creators";
export type LayoutMode = "carousel" | "grid" | "list";

export interface HomeRow {
  id: string;
  title: string;
  subtitle?: string;
  stations: Station[];
  type: "trending" | "genre" | "curated" | "outdoor";
  filterTag?: string;
}

// --- Podcast Specific Types ---

export type PodcastCategory =
  | "fishing_outdoor"
  | "tech_ai_software"
  | "economy_business"
  | "sports"
  | "cooking_food_wine"
  | "other";

export interface SeedPod {
  id: string;
  title: string;
  rssUrl: string;
  language: string;
  country: string;
  state?: string; // New: Supports region display logic
  categories: string[];
  internalCategory: PodcastCategory; // Strict mapping
  source: "manual" | "auto" | "curator";
  imageUrl?: string;
  description?: string;
}

export interface Episode {
  title: string;
  description: string;
  pubDate: string;
  audioUrl: string;
  duration?: string;
  guid?: string; // For tracking state
  imageUrl?: string; // Added for per-episode artwork
}

export interface Podcast extends SeedPod {
  description?: string;
  imageUrl?: string;
  link?: string; // NEW: Actual website URL (from RSS <link> tag)
  status: "active" | "invalid" | "loading";
  episodes: Episode[];
  qualityScore?: number;
  totalEpisodes?: number; // Added for filtering
}

export interface UserPodcastState {
  userId: string;
  podcastId: string;
  lastPlayedEpisodeUrl?: string;
  lastPositionSeconds: number;
  isFollowed: boolean;
}

export interface PodcastReview {
  id: string;
  created_at: string;
  user_id: string;
  podcast_id: string;
  episode_guid: string;
  rating: number; // 1-5 stars
  review_text?: string;
  user_name?: string;
  user_avatar?: string;
}

export interface ReviewStats {
  average_rating: number;
  total_reviews: number;
  rating_distribution: { [key: number]: number }; // { 5: 10, 4: 5, 3: 2, 2: 1, 1: 0 }
}

export interface SupabaseStationRow {
  id: string;
  name: string;
  country: string;
  country_code: string;
  state: string;
  provider: string;
  stream_url: string;
  website: string;
  logo_url: string;
  bitrate: number;
  codec: string;
  language: string;
  tags: string[];
  votes: number;
  click_trend: number;
  online_status: boolean;
}

export interface SupabasePodcastRow {
  id: string;
  title: string;
  rss_url: string;
  image_url: string;
  country: string;
  language: string;
  categories: string[];
  description: string;
  provider: string;
}

export interface PodcastIndexFeed {
  id: number;
  title: string;
  url: string;
  description: string;
  image: string;
  country?: string;
  language: string;
  categories?: Record<string, string>;
  episodeCount?: number;
  itunesId?: number | null;
  link?: string;
  originalUrl?: string; // Sometimes returned
  author?: string;
  ownerName?: string;
  newestItemPubdate?: number;
}

export interface Rss2JsonItem {
  title: string;
  pubDate: string;
  link: string;
  guid: string;
  author: string;
  thumbnail: string;
  description: string;
  content: string;
  enclosure: {
    link: string;
    type: string;
    length: number;
    duration: number;
    thumbnail?: string;
  };
  categories: string[];
}

export interface Rss2JsonData {
  status: string;
  feed: {
    url: string;
    title: string;
    link: string;
    author: string;
    description: string;
    image: string;
  };
  items: Rss2JsonItem[];
}

export interface RadioBrowserStation {
  stationuuid: string;
  name: string;
  url_resolved: string;
  url: string;
  homepage: string;
  favicon: string;
  tags: string;
  country: string;
  countrycode: string;
  state: string;
  language: string;
  votes: number;
  clickcount: number;
  clicktrend: number;
  lastcheckok: number;
  bitrate: number;
  codec: string;
}

export interface RadioBrowserCountry {
  name: string;
  stationcount: number;
  iso_3166_1?: string;
}

// --- Global Carousel Types ---

export interface CreatorChannel {
  id: string;
  name: string;
  tagline: string;
  status: "coming-soon" | "live";
}

export interface GlobalCarousel {
  id: string;
  name: string;
  title: string;
  subtitle: string;
  type: HomeRow["type"];
  stationIds: string[]; // Legacy: will be deprecated
  filter_rules?: {
    // NEW: Dynamic filter rules (snake_case to match DB)
    type: "dynamic" | "manual" | "hybrid";
    tag?: string;
    country?: string;
    limit?: number;
    sort?: "votes" | "clickTrend" | "name" | "bitrate";
    stationIds?: string[];
  };
  countries: string[]; // 'All' or specific countries ['Sweden', 'Finland']
  position: string;
  section: "radio" | "podcast";
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PodcastSubscription {
  id: string;
  user_id: string;
  podcast_id: string;
  last_known_episode_published_at?: string;
  notify_enabled: boolean;
  created_at: string;
}

export interface PushSubscriptionRecord {
  id: string;
  user_id: string;
  endpoint: string;
  p256dh: string;
  auth: string;
  created_at: string;
}

// Re-export other files in types directory
export * from "./stationInfo";
export * from "./stationMeta";
